import { Button, Col, Divider, Popover, Row, Space } from "antd";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import AnimatedCharacters from "../animation/animatedText";
import { scrollToSection } from "../animation/smoothScroll";
import IMG from "../assets/image/my_image_2.png";

// ─── Typewriter hook ───────────────────────────────────────────────────────────
const useTypewriter = (texts, speed = 60, pause = 1800) => {
  const [display, setDisplay] = useState("");
  const [textIndex, setTextIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = texts[textIndex];
    let timeout;

    if (!deleting && charIndex < current.length) {
      timeout = setTimeout(() => setCharIndex((c) => c + 1), speed);
    } else if (!deleting && charIndex === current.length) {
      timeout = setTimeout(() => setDeleting(true), pause);
    } else if (deleting && charIndex > 0) {
      timeout = setTimeout(() => setCharIndex((c) => c - 1), speed / 2);
    } else if (deleting && charIndex === 0) {
      setDeleting(false);
      setTextIndex((t) => (t + 1) % texts.length);
    }

    setDisplay(current.substring(0, charIndex));
    return () => clearTimeout(timeout);
  }, [charIndex, deleting, textIndex, texts, speed, pause]);

  return display;
};

// ─── Floating Badge ────────────────────────────────────────────────────────────
// const FloatingBadge = ({ icon, label, style, delay = 0 }) => (
//   <motion.div
//     initial={{ opacity: 0, scale: 0, y: 20 }}
//     animate={{ opacity: 1, scale: 1, y: 0 }}
//     transition={{ delay, duration: 0.5, type: "spring", stiffness: 200 }}
//     style={{
//       position: "absolute",
//       background: "#ffffff",
//       border: "2px solid var(--bg-dark)",
//       boxShadow: "4px 4px 0px var(--accent-color)",
//       padding: "8px 14px",
//       display: "flex",
//       alignItems: "center",
//       gap: 8,
//       zIndex: 99999,
//       fontSize: 13,
//       fontWeight: 700,
//       color: "var(--text-primary)",
//       whiteSpace: "nowrap",
//       ...style,
//     }}
//   >
//     <motion.span
//       animate={{ rotate: [0, -10, 10, -10, 0] }}
//       transition={{ delay: delay + 1, duration: 0.6, repeat: Infinity, repeatDelay: 4 }}
//       style={{ fontSize: 18 }}
//     >
//       {icon}
//     </motion.span>
//     {label}
//   </motion.div>
// );

// ─── Scroll indicator ──────────────────────────────────────────────────────────
const ScrollIndicator = () => (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 2.5, duration: 0.8 }}
    style={{
      position: "absolute",
      bottom: 30,
      left: "50%",
      transform: "translateX(-50%)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 6,
      cursor: "pointer",
      zIndex: 10,
    }}
    onClick={() => scrollToSection("#about", -60)}
  >
    <motion.span
      style={{ fontSize: 11, letterSpacing: 3, color: "var(--text-muted)", textTransform: "uppercase" }}
    >
      Scroll
    </motion.span>
    <motion.div
      style={{
        width: 26,
        height: 42,
        border: "2px solid var(--text-muted)",
        borderRadius: 13,
        display: "flex",
        justifyContent: "center",
        paddingTop: 6,
      }}
    >
      <motion.div
        animate={{ y: [0, 12, 0] }}
        transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        style={{
          width: 4,
          height: 8,
          background: "var(--accent-color)",
          borderRadius: 2,
        }}
      />
    </motion.div>
  </motion.div>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const Home = ({ showModal }) => {
  const sectionRef = useRef(null);
  const imageRef = useRef(null);

  // Scroll-linked values
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  const yText    = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const yImage   = useTransform(scrollYProgress, [0, 1], ["0%", "15%"]);
  const opacity  = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const scale    = useTransform(scrollYProgress, [0, 1], [1, 0.92]);
  const rotate   = useTransform(scrollYProgress, [0, 1], [0, -3]);

  const springY     = useSpring(yText,  { stiffness: 80, damping: 20 });
  const springScale = useSpring(scale,  { stiffness: 80, damping: 20 });

  // Magnetic image mouse tracking
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const imgX   = useSpring(useTransform(mouseX, [-300, 300], [-12, 12]), { stiffness: 150, damping: 25 });
  const imgY   = useSpring(useTransform(mouseY, [-300, 300], [-12, 12]), { stiffness: 150, damping: 25 });

  const handleMouseMove = (e) => {
    if (!imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    mouseX.set(e.clientX - rect.left - rect.width / 2);
    mouseY.set(e.clientY - rect.top  - rect.height / 2);
  };
  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  // Typewriter
  const typeText = useTypewriter([
    "Full-stack Developer",
    "Real-time Systems Engineer",
    "IoT Solutions Builder",
    "MERN Stack Specialist",
  ]);

  // Stagger variants
  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12, delayChildren: 0.6 } },
  };

  const itemVariants = {
    hidden:  { opacity: 0, y: 40, filter: "blur(6px)" },
    visible: {
      opacity: 1, y: 0, filter: "blur(0px)",
      transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    },
  };

  // const nameVariants = {
  //   hidden: {},
  //   visible: { transition: { staggerChildren: 0.025 } },
  // };

  const placeholderText = [
    { type: "heading2", text: "Hello, I am" },
    { type: "heading1", text: "HEIN HTET" },
  ];

  const nameContainer = {
    visible: { transition: { staggerChildren: 0.025 } },
  };

  return (
    <div ref={sectionRef} style={{ position: "relative", height: "100%" }}>

      {/* Animated background grid lines */}
      <motion.div
        style={{ opacity }}
        aria-hidden="true"
      >
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ scaleY: 0, opacity: 0 }}
            animate={{ scaleY: 1, opacity: 0.04 }}
            transition={{ delay: i * 0.1, duration: 1.2, ease: "easeOut" }}
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: `${(i + 1) * (100 / 7)}%`,
              width: 1,
              background: "var(--bg-dark)",
              transformOrigin: "top",
              zIndex: 0,
            }}
          />
        ))}
        {[...Array(4)].map((_, i) => (
          <motion.div
            key={`h-${i}`}
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 0.04 }}
            transition={{ delay: 0.3 + i * 0.15, duration: 1.2, ease: "easeOut" }}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: `${(i + 1) * 20}%`,
              height: 1,
              background: "var(--bg-dark)",
              transformOrigin: "left",
              zIndex: 0,
            }}
          />
        ))}
      </motion.div>

      <Row align="middle" style={{ height: "100%", position: "relative", zIndex: 2 }}>

        {/* ── Left column ── */}
        <Col span={12} style={{ padding: "5%" }}>
          <motion.div style={{ y: springY, scale: springScale, opacity }}>

            {/* Animated name */}
            <motion.div
              className="App"
              initial="hidden"
              animate="visible"
              variants={nameContainer}
            >
              {placeholderText.map((item, index) => (
                <AnimatedCharacters {...item} key={index} />
              ))}
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >

              {/* Typewriter role */}
              <motion.div variants={itemVariants} style={{ marginBottom: "1.2rem", minHeight: 56 }}>
                <p style={{
                  fontWeight: 900,
                  fontSize: "2rem",
                  color: "var(--bg-dark)",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  margin: 0,
                }}>
                  {typeText}
                  <motion.span
                    animate={{ opacity: [1, 0, 1] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                    style={{ color: "var(--accent-color)", marginLeft: 2 }}
                  >|</motion.span>
                </p>
                <motion.p style={{
                  fontSize: "1.4rem",
                  color: "var(--accent-color)",
                  fontWeight: 600,
                  margin: 0,
                  marginTop: 4,
                }}>
                  Specializing in Real-time & IoT Systems
                </motion.p>
              </motion.div>

              {/* Description */}
              <motion.p
                variants={itemVariants}
                style={{
                  fontSize: 17,
                  fontWeight: 300,
                  color: "var(--text-secondary)",
                  lineHeight: "1.8",
                  marginBottom: "1.5rem",
                }}
              >
                Building scalable React + Node.js applications, production-ready APIs,
                and real-time dashboards. Experienced in{" "}
                <motion.strong
                  style={{ color: "var(--primary-color)" }}
                  whileHover={{ letterSpacing: "1px" }}
                  transition={{ duration: 0.2 }}
                >
                  IoT alert systems
                </motion.strong>
                ,{" "}
                <motion.strong
                  style={{ color: "var(--primary-color)" }}
                  whileHover={{ letterSpacing: "1px" }}
                  transition={{ duration: 0.2 }}
                >
                  Socket.io integrations
                </motion.strong>
                , and high-performance backends.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div variants={itemVariants}>
                <Space size={15}>
                  <motion.div whileTap={{ x: 2, y: 2 }}>
                    <Button
                      size="large"
                      type="primary"
                      style={{ height: 50,
                        background:"var(--primary-color)",
                        padding: "0 30px", fontSize: 16, borderRadius: 0, fontWeight: "bold" }}
                    >
                      <a href="#contact" style={{ color: "white" }}>
                        Contact Me
                      </a>
                    </Button>
                  </motion.div>
                  <motion.div whileTap={{ x: 2, y: 2 }}>
                    <Button
                      size="large"
                      className="btn-outline"
                      style={{ height: 50, borderRadius: 0 }}
                    >
                      <a href="/Hein_Htet_CV.pdf" download style={{ color: "var(--text-primary)" }}>
                        <i className="fa fa-download" style={{ paddingRight: 10 }} />
                        Download CV
                      </a>
                    </Button>
                  </motion.div>
                </Space>
              </motion.div>

              {/* Social links */}
              <motion.p
                variants={itemVariants}
                style={{ fontWeight: 300, fontSize: 16, color: "var(--text-muted)", marginTop: 24, marginBottom: 4 }}
              >
                Find me on
              </motion.p>

              <motion.div variants={itemVariants} style={{ fontSize: 32, display: "flex", gap: 4 }}>
                {[
                  { href: "mailto:heinhtet25944@gmail.com", icon: "fa fa-envelope", title: null },
                  { href: "https://www.linkedin.com/in/hein-htet-2a34241a3", icon: "fa-brands fa-linkedin", title: null },
                  { href: "https://github.com/NewDivide41194", icon: "fa-brands fa-github", title: null },
                ].map((social, i) => (
                  <motion.a
                    key={i}
                    href={social.href}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "var(--text-primary)", padding: "0 10px" }}
                    whileHover={{ y: -6, color: "var(--primary-color)", scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 400, damping: 15 }}
                  >
                    <i className={social.icon} />
                  </motion.a>
                ))}
                <Popover content="+66 0988477743" title="Whatsapp Number">
                  <motion.span
                    style={{ color: "var(--text-primary)", padding: "0 10px", cursor: "pointer", fontSize: 32 }}
                    whileHover={{ y: -6, color: "var(--primary-color)", scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 400, damping: 15 }}
                  >
                    <i className="fa-brands fa-whatsapp" />
                  </motion.span>
                </Popover>
              </motion.div>

            </motion.div>

          </motion.div>

          <Divider style={{ background: "var(--bg-dark)", opacity: 0.2, height: 4 }} />
        </Col>

        {/* ── Right column — Image ── */}
        <Col
          className="img-col"
          span={12}
          style={{ textAlign: "center", position: "relative" }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          ref={imageRef}
        >
          <motion.div style={{ y: yImage, opacity, rotate }} >

            {/* Floating background square */}
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 0.25, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.4 }}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "78%",
                height: "78%",
                background: "var(--highlight-color)",
                zIndex: 0,
              }}
            />

            {/* Decorative orbit ring */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: "85%",
                height: "85%",
                transform: "translate(-50%, -50%)",
                border: "1px dashed rgba(255, 170, 0, 0.78)",
                borderRadius: "50%",
                zIndex: 0,
              }}
            />

            {/* Magnetic image */}
            <motion.div
              style={{ x: imgX, y: imgY, position: "relative", display: "inline-block" }}
              initial={{ opacity: 0, scale: 0.85, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              <img
                src={IMG}
                alt="Hein Htet"
                style={{
                  maxHeight: "85vh",
                  maxWidth: "100%",
                  paddingTop: 20,
                  position: "relative",
                  zIndex: 1,
                  // boxShadow: "20px 20px 0px var(--accent-color)",
                  // border: "4px solid var(--bg-dark)",
                  borderRadius: 0,
                }}
              />
            </motion.div>
          </motion.div>
        </Col>

      </Row>

      <ScrollIndicator />
    </div>
  );
};

export default Home;
